import React, { useState, useEffect } from 'react'
import { 
  ArrowLeft,
  Save,
  RotateCcw
} from 'lucide-react'
import Breadcrumb from '../../components/Common/Breadcrumb'
import { useNavigate, useLocation } from 'react-router-dom'
import { toast } from 'react-toastify'
import overtimeService from '../../services/overtimeService'
import employeeMasterService from '../../services/employeeMasterService'
import projectService from '../../services/projectService'

const OvertimeForm = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const isEditMode = location.pathname.includes('/edit')
  const isViewMode = location.pathname.includes('/view')
  const overtimeData = location.state?.overtimeData

  const [formData, setFormData] = useState({
    overtimeNumber: '',
    employee: '',
    employeeName: '',
    employeeCode: '',
    department: '',
    overtimeDate: new Date().toISOString().split('T')[0],
    regularHours: 0,
    overtimeHours: 0,
    overtimeType: 'Weekday',
    startTime: '',
    endTime: '',
    project: '',
    projectCode: '',
    projectName: '',
    activity: '',
    taskDescription: '',
    compensationType: 'Overtime Pay',
    hourlyRate: 0,
    overtimeMultiplier: 1.5,
    timeOffHours: 0,
    currency: 'INR',
    reason: '',
    notes: '',
    status: 'Draft'
  })

  const [employees, setEmployees] = useState([])
  const [projects, setProjects] = useState([])
  const [isLoadingOvertimeNumber, setIsLoadingOvertimeNumber] = useState(true)
  const [isLoading, setIsLoading] = useState(false)

  // Fetch next overtime number (only for new records)
  useEffect(() => {
    if (!isEditMode && !isViewMode) {
      const fetchOvertimeNumber = async () => {
        try {
          setIsLoadingOvertimeNumber(true)
          const response = await overtimeService.getNextOvertimeNumber()
          if (response.success && response.data) {
            setFormData(prev => ({
              ...prev,
              overtimeNumber: response.data.overtimeNumber || response.data.formattedValue || 'OT0001'
            }))
          }
        } catch (error) {
          console.error('Error fetching overtime number:', error)
          setFormData(prev => ({ ...prev, overtimeNumber: 'OT0001' }))
        } finally {
          setIsLoadingOvertimeNumber(false)
        }
      }
      fetchOvertimeNumber()
    }
  }, [isEditMode, isViewMode])

  // Load employees and projects
  useEffect(() => {
    loadEmployees()
    loadProjects()
  }, [])

  // Load overtime data if editing or viewing
  useEffect(() => {
    if ((isEditMode || isViewMode) && overtimeData?._id) {
      loadOvertimeData(overtimeData._id)
    }
  }, [isEditMode, isViewMode, overtimeData])

  const loadEmployees = async () => {
    try {
      const response = await employeeMasterService.getAllEmployees()
      if (response.success) {
        setEmployees(response.data || [])
      }
    } catch (error) {
      console.error('Error loading employees:', error)
    }
  }

  const loadProjects = async () => {
    try {
      const response = await projectService.getAllProjects({ limit: 1000 })
      if (response.success) {
        setProjects(response.data || [])
      }
    } catch (error) {
      console.error('Error loading projects:', error)
    }
  }

  const loadOvertimeData = async (id) => {
    try {
      setIsLoading(true)
      const response = await overtimeService.getOvertimeById(id)
      if (response.success && response.data) {
        const overtime = response.data
        setFormData({
          overtimeNumber: overtime.overtimeNumber || '',
          employee: overtime.employee?._id || overtime.employee || '',
          employeeName: overtime.employeeName || overtime.employee?.employeeName || '',
          employeeCode: overtime.employeeCode || overtime.employee?.employeeCode || '',
          department: overtime.department || overtime.employee?.department || '',
          overtimeDate: overtime.overtimeDate ? new Date(overtime.overtimeDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
          regularHours: overtime.regularHours || 0,
          overtimeHours: overtime.overtimeHours || 0,
          overtimeType: overtime.overtimeType || 'Weekday',
          startTime: overtime.startTime ? new Date(overtime.startTime).toISOString().slice(0, 16) : '',
          endTime: overtime.endTime ? new Date(overtime.endTime).toISOString().slice(0, 16) : '',
          project: overtime.project?._id || overtime.project || '',
          projectCode: overtime.projectCode || overtime.project?.projectCode || '',
          projectName: overtime.projectName || overtime.project?.projectName || '',
          activity: overtime.activity || '',
          taskDescription: overtime.taskDescription || '',
          compensationType: overtime.compensationType || 'Overtime Pay',
          hourlyRate: overtime.hourlyRate || 0,
          overtimeMultiplier: overtime.overtimeMultiplier || 1.5,
          timeOffHours: overtime.timeOffHours || 0,
          currency: overtime.currency || 'INR',
          reason: overtime.reason || '',
          notes: overtime.notes || '',
          status: overtime.status || 'Draft'
        })
      }
    } catch (error) {
      console.error('Error loading overtime record:', error)
      toast.error('Failed to load overtime data')
    } finally {
      setIsLoading(false)
    }
  }

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target
    
    if (name === 'employee') {
      const selectedEmployee = employees.find(emp => emp._id === value)
      setFormData(prev => ({
        ...prev,
        employee: value,
        employeeName: selectedEmployee?.employeeName || '',
        employeeCode: selectedEmployee?.employeeCode || '',
        department: selectedEmployee?.department || ''
      }))
    } else if (name === 'project') {
      const selectedProject = projects.find(proj => proj._id === value)
      setFormData(prev => ({
        ...prev,
        project: value,
        projectCode: selectedProject?.projectNumber || '',
        projectName: selectedProject?.projectName || ''
      }))
    } else if (type === 'checkbox') {
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }))
    } else if (type === 'number') {
      setFormData(prev => ({
        ...prev,
        [name]: parseFloat(value) || 0
      }))
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }))
    }
  }

  const calculateCompensation = () => {
    if ((formData.compensationType === 'Overtime Pay' || formData.compensationType === 'Both') 
        && formData.hourlyRate && formData.overtimeHours) {
      return (formData.hourlyRate * formData.overtimeMultiplier * formData.overtimeHours).toFixed(2)
    }
    return 0
  }

  const handleSave = async () => {
    try {
      // Validation
      if (!formData.employee || !formData.overtimeDate) {
        toast.error('Please select employee and overtime date')
        return
      }
      if (formData.overtimeHours <= 0) {
        toast.error('Please enter overtime hours')
        return
      }

      setIsLoading(true)
      
      if (isEditMode && overtimeData?._id) {
        const response = await overtimeService.updateOvertime(overtimeData._id, formData)
        if (response.success) {
          toast.success('Overtime record updated successfully!')
          navigate('/timesheets/overtime')
        } else {
          toast.error(response.message || 'Failed to update overtime record')
        }
      } else {
        const response = await overtimeService.createOvertime(formData)
        if (response.success) {
          toast.success('Overtime record created successfully!')
          navigate('/timesheets/overtime')
        } else {
          toast.error(response.message || 'Failed to create overtime record')
        }
      }
    } catch (error) {
      console.error('Error saving overtime record:', error)
      if (error.response?.status === 400) {
        toast.error(error.response.data?.message || 'Invalid data provided')
      } else {
        if (!error._errorHandled) {
          toast.error('Failed to save overtime record')
        }
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleReset = () => {
    if (isEditMode || isViewMode) {
      loadOvertimeData(overtimeData._id)
    } else {
      setFormData(prev => ({
        ...prev,
        employee: '',
        employeeName: '',
        employeeCode: '',
        department: '',
        project: '',
        projectCode: '',
        projectName: '',
        activity: '',
        taskDescription: '',
        regularHours: 0,
        overtimeHours: 0,
        overtimeType: 'Weekday',
        startTime: '',
        endTime: '',
        compensationType: 'Overtime Pay',
        hourlyRate: 0,
        overtimeMultiplier: 1.5,
        timeOffHours: 0,
        reason: '',
        notes: '',
        status: 'Draft'
      }))
    }
  }

  const breadcrumbItems = [
    { label: 'Home', path: '/' },
    { label: 'Timesheets', path: '/timesheets' },
    { label: 'Overtime', path: '/timesheets/overtime' },
    { 
      label: isEditMode ? 'Edit Overtime' : isViewMode ? 'View Overtime' : 'New Overtime', 
      path: location.pathname, 
      isCurrent: true 
    }
  ]

  if (isLoading && (isEditMode || isViewMode)) {
    return (
      <div className="sales-dashboard">
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="sr-only">Loading...</span>
          </div>
          <p style={{ marginTop: '16px', color: 'var(--color-text-secondary, #5B6478)' }}>Loading overtime record...</p>
        </div>
      </div>
    )
  }

  const compensationAmount = calculateCompensation()
  const totalHours = (formData.regularHours || 0) + (formData.overtimeHours || 0)

  return (
    <div className="sales-dashboard">
      <Breadcrumb items={breadcrumbItems} />

      <div className="page-header">
        <div className="page-title">
          <h1>{isEditMode ? 'Edit Overtime' : isViewMode ? 'View Overtime' : 'New Overtime'}</h1>
          <p>{isEditMode ? 'Update overtime record details' : isViewMode ? 'View overtime record details' : 'Create a new overtime record'}</p>
        </div>
        <div className="page-actions">
          <button 
            className="btn btn-secondary" 
            onClick={() => navigate('/timesheets/overtime')}
            style={{
              padding: '10px 20px',
              border: '1px solid #e9ecef',
              borderRadius: '6px',
              background: '#fff',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontFamily: 'Inter, sans-serif',
              fontSize: '14px',
              color: '#495057'
            }}
          >
            <ArrowLeft size={16} />
            Back
          </button>
        </div>
      </div>

      <div className="form-container">
        <div className="sac-form-header">
          <h2>Overtime Information</h2>
        </div>
          
        <form className="form-content">
          <div className="section-header"><h3>Overtime Details</h3></div>
          {/* Row 1: Overtime Number, Employee, Overtime Date, Overtime Type */}
          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="overtimeNumber" className="form-label required">
                Overtime Number
              </label>
              <input
                type="text"
                id="overtimeNumber"
                name="overtimeNumber"
                value={isLoadingOvertimeNumber ? 'Generating...' : formData.overtimeNumber}
                readOnly
                className="form-input"
                style={{ backgroundColor: '#f9fafb', color: 'var(--color-text-secondary, #5B6478)', cursor: 'not-allowed' }}
              />
            </div>

            <div className="form-group">
              <label htmlFor="employee" className="form-label required">
                Employee
              </label>
              <select
                id="employee"
                name="employee"
                value={formData.employee}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
                required
              >
                <option value="">Select Employee</option>
                {employees.map(emp => (
                  <option key={emp._id} value={emp._id}>
                    {emp.employeeName} ({emp.employeeCode})
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="overtimeDate" className="form-label required">
                Overtime Date
              </label>
              <input
                type="date"
                id="overtimeDate"
                name="overtimeDate"
                value={formData.overtimeDate}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="overtimeType" className="form-label">
                Overtime Type
              </label>
              <select
                id="overtimeType"
                name="overtimeType"
                value={formData.overtimeType}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
              >
                <option value="Weekday">Weekday</option>
                <option value="Weekend">Weekend</option>
                <option value="Holiday">Holiday</option>
                <option value="Night Shift">Night Shift</option>
              </select>
            </div>
          </div>

          {/* Row 2: Regular Hours, Overtime Hours, Start Time, End Time */}
          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="regularHours" className="form-label">
                Regular Hours
              </label>
              <input
                type="number"
                id="regularHours"
                name="regularHours"
                value={formData.regularHours}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
                min="0"
                step="0.5"
              />
            </div>

            <div className="form-group">
              <label htmlFor="overtimeHours" className="form-label required">
                Overtime Hours
              </label>
              <input
                type="number"
                id="overtimeHours"
                name="overtimeHours"
                value={formData.overtimeHours}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
                min="0"
                step="0.5"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="startTime" className="form-label">
                Start Time
              </label>
              <input
                type="datetime-local"
                id="startTime"
                name="startTime"
                value={formData.startTime}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="endTime" className="form-label">
                End Time
              </label>
              <input
                type="datetime-local"
                id="endTime"
                name="endTime"
                value={formData.endTime}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
              />
            </div>
          </div>

          {/* Row 3: Project, Activity */}
          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="project" className="form-label">
                Project
              </label>
              <select
                id="project"
                name="project"
                value={formData.project}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
              >
                <option value="">Select Project</option>
                {projects.map(proj => (
                  <option key={proj._id} value={proj._id}>
                    {proj.projectName} ({proj.projectNumber})
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="activity" className="form-label">
                Activity
              </label>
              <input
                type="text"
                id="activity"
                name="activity"
                value={formData.activity}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
                placeholder="e.g., Emergency Support"
              />
            </div>

            <div className="form-group">
              <label htmlFor="status" className="form-label">
                Status
              </label>
              <select
                id="status"
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
              >
                <option value="Draft">Draft</option>
                <option value="Submitted">Submitted</option>
                <option value="Approved">Approved</option>
                <option value="Rejected">Rejected</option>
                <option value="Paid">Paid</option>
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">
                Total Hours
              </label>
              <input
                type="text"
                value={`${totalHours.toFixed(1)}h`}
                readOnly
                className="form-input"
                style={{ backgroundColor: '#f9fafb', color: 'var(--color-text-secondary, #5B6478)', cursor: 'not-allowed' }}
              />
            </div>
          </div>

          {/* Compensation Section */}
          <div className="section-header"><h3>Compensation Details</h3></div>

          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="compensationType" className="form-label">
                Compensation Type
              </label>
              <select
                id="compensationType"
                name="compensationType"
                value={formData.compensationType}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
              >
                <option value="Time Off">Time Off</option>
                <option value="Overtime Pay">Overtime Pay</option>
                <option value="Both">Both</option>
                <option value="None">None</option>
              </select>
            </div>

            {(formData.compensationType === 'Overtime Pay' || formData.compensationType === 'Both') && (
              <>
                <div className="form-group">
                  <label htmlFor="hourlyRate" className="form-label">
                    Hourly Rate
                  </label>
                  <input
                    type="number"
                    id="hourlyRate"
                    name="hourlyRate"
                    value={formData.hourlyRate}
                    onChange={handleInputChange}
                    disabled={isViewMode}
                    className="form-input"
                    min="0"
                    step="0.01"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="overtimeMultiplier" className="form-label">
                    Overtime Multiplier
                  </label>
                  <input
                    type="number"
                    id="overtimeMultiplier"
                    name="overtimeMultiplier"
                    value={formData.overtimeMultiplier}
                    onChange={handleInputChange}
                    disabled={isViewMode}
                    className="form-input"
                    min="1"
                    step="0.1"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="currency" className="form-label">
                    Currency
                  </label>
                  <select
                    id="currency"
                    name="currency"
                    value={formData.currency}
                    onChange={handleInputChange}
                    disabled={isViewMode}
                    className="form-input"
                  >
                    <option value="INR">INR</option>
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                    <option value="GBP">GBP</option>
                  </select>
                </div>
              </>
            )}

            {(formData.compensationType === 'Time Off' || formData.compensationType === 'Both') && (
              <div className="form-group">
                <label htmlFor="timeOffHours" className="form-label">
                  Time Off Hours
                </label>
                <input
                  type="number"
                  id="timeOffHours"
                  name="timeOffHours"
                  value={formData.timeOffHours}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  className="form-input"
                  min="0"
                  step="0.5"
                />
              </div>
            )}
          </div>

          {compensationAmount > 0 && (
            <div style={{ 
              background: '#f8f9fa', 
              border: '1px solid #e9ecef', 
              borderRadius: '6px', 
              padding: '16px',
              marginBottom: '24px'
            }}>
              <div style={{ fontSize: '14px', color: 'var(--color-text-secondary, #5B6478)', marginBottom: '4px' }}>Calculated Compensation Amount</div>
              <div style={{ fontSize: '20px', fontWeight: '600', color: 'var(--color-brand-navy, #222448)' }}>
                {new Intl.NumberFormat('en-IN', {
                  style: 'currency',
                  currency: formData.currency,
                  minimumFractionDigits: 2
                }).format(compensationAmount)}
              </div>
            </div>
          )}

          {/* Task Description */}
          <div className="form-group">
            <label htmlFor="taskDescription" className="form-label">
              Task Description
            </label>
            <textarea
              id="taskDescription"
              name="taskDescription"
              value={formData.taskDescription}
              onChange={handleInputChange}
              disabled={isViewMode}
              className="form-input"
              rows="3"
              placeholder="Describe the work performed during overtime..."
            />
          </div>

          {/* Reason */}
          <div className="form-group">
            <label htmlFor="reason" className="form-label">
              Reason for Overtime
            </label>
            <textarea
              id="reason"
              name="reason"
              value={formData.reason}
              onChange={handleInputChange}
              disabled={isViewMode}
              className="form-input"
              rows="2"
              placeholder="Reason for working overtime..."
            />
          </div>

          {/* Notes */}
          <div className="form-group">
            <label htmlFor="notes" className="form-label">
              Notes
            </label>
            <textarea
              id="notes"
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              disabled={isViewMode}
              className="form-input"
              rows="3"
              placeholder="Additional notes..."
            />
          </div>

          {/* Action Buttons */}
          {!isViewMode && (
            <div className="form-actions">
              <button
                type="button"
                onClick={handleSave}
                disabled={isLoading}
                className="btn btn-primary"
                style={{
                  padding: '10px 24px',
                  background: '#292752',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '14px',
                  fontWeight: '500',
                  opacity: isLoading ? 0.6 : 1
                }}
              >
                <Save size={16} />
                {isLoading ? 'Saving...' : 'Save'}
              </button>
              <button
                type="button"
                onClick={handleReset}
                disabled={isLoading}
                className="btn btn-secondary"
                style={{
                  padding: '10px 24px',
                  border: '1px solid #e9ecef',
                  borderRadius: '6px',
                  background: '#fff',
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '14px',
                  color: '#495057'
                }}
              >
                <RotateCcw size={16} />
                Reset
              </button>
            </div>
          )}
        </form>
      </div>
    </div>
  )
}

export default OvertimeForm

